// 檢視課程大 modal（course-clazz-query-list；B2-3＝legacy `#courseModal` 4 分頁聚合版）
// ＋首頁模式（B2-6）＋行事曆項目入口（B2-3 的 legacy 入口＝活動點擊，clazzQueryList.js:1177）。
//
// legacy 對照（行號親驗 2026-09-22；詳 docs/plans/2026-09-22-batch11-course-modal-progress.md §3）：
//   - courseModal.jsp:80/:87-102/:119：標題「檢視課程」；4 分頁 課程資訊/課程表單/課程公告/課堂點名；
//     footer 關閉（spring close＝關閉，properties:45）。
//   - courseModal.js:42-55 關閉守衛：點名分頁未存檔 → sysConfirm「尚未存檔，是否確定要關閉?」；
//     :57-72 切離點名分頁也有同樣確認（legacy 用瀏覽器 confirm()＝quirk，雛形一律 dialogs.confirm）。
//   - :123-135 分頁可見性（courseQuery 來源依權限/部門藏點名分頁）——雛形無權限模型，
//     四個分頁全顯示【雛形值待實機校準】。
//   - courseInfoTemplate.jsp:2-220（唯讀欄位 label 群）＋courseModal.js:139-190（附檔表/教師清單/
//     其他參與訓練類別）。
//   - courseFormTab.js:15-24 手動取單「是否確定要發表單給自己?」→ genCourseForm；表列六欄
//     （學生/表單名稱/應完成日期/表單流程/表單狀態/表單版本，tabs/courseFormTab.jsp:15-20）。
//   - announcementTab.jsp:10-21（新增＋刪除/公告狀態/主旨/發佈日期）＋announcementTab.js:42
//     刪除 confirm「是否確定要刪除?」。
//   - 課堂點名分頁＝rollCallTab.jsp＋rollCallTab.js（本體重用 RollCallPanel，批次 11 抽取）。
//
// 雛形資料策略：row.attendance/rollCallCode/forms/announcements 存列上（既有種子＋update op，
// 與查詢頁分立 modal 同源）；rollCallCompleted 以「點名列已有非未點名狀態或本 session 存過檔」
// 近似 checkRollCallStatusAjax【雛形值待實機校準】。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useRef, useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { Table, type TableColumn, type TableRow } from '@/components/base/Table';
import { useDataSource } from '@/mocks';
import type { Row } from '@/mocks/types';
import { legacyAlertFallback, type BehaviorFlowContext } from '../templates/page-behaviors';
import {
  ACCOUNT_JOB_TYPE_OPTIONS,
  COURSE_CLAZZ_KIND_OPTIONS,
  COURSE_CLAZZ_LOCATION_OPTIONS,
} from '@/mocks/resource-rules';
import {
  AnnouncementModal,
  CourseInfoModal,
  RollCallPanel,
  toKebabLocal,
} from './ClassQueryModals';

/** 首頁模式的固定「今天」（fixtures DATE_EPOCH_MS＝2026/01/05；不用 Date.now()）。 */
const HOME_NOW = '2026/01/05';
/** 最近一個月＝固定今天起 30 天（legacy getClassListForHomepageAjax 的伺服器窗，雛形以窗比較）。 */
const HOME_WINDOW_END = '2026/02/04';

type CourseViewTab = 'info' | 'form' | 'announcement' | 'rollcall';

const TAB_LABELS: readonly { key: CourseViewTab; id: string; label: string }[] = [
  { key: 'info', id: 'tab-course-info', label: ' 課程資訊' }, // courseModal.jsp:87（前導空格照實）
  { key: 'form', id: 'tab-course-form', label: '課程表單' }, // :79
  { key: 'announcement', id: 'tab-course-announcement', label: '課程公告' }, // :84
  { key: 'rollcall', id: 'tab-course-roll-call', label: '課堂點名' }, // :89
];

const labelOf = (pool: readonly { value: string; label: string }[], value: string) =>
  pool.find((option) => option.value === value)?.label ?? '';

export function CourseViewModal({
  pageId,
  row,
  onClose,
}: {
  pageId: string;
  row: Row;
  onClose(): void;
}): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [activeTab, setActiveTab] = useState<CourseViewTab>('info'); // :121 預設課程資訊
  const [rollCallCompleted, setRollCallCompleted] = useState<boolean>(
    // checkRollCallStatusAjax（classUtils.js:245-258）的鏡像：rollCallCompleted 旗標
    // （種子；存檔後置 1）。【雛形值待實機校準】
    String(row['rollCallCompleted'] ?? '0') === '1',
  );

  const rollCallSaveRef = useRef<(() => void) | null>(null);
  const rowKey = String(row.id ?? '');

  /** 是否需要「尚未存檔」守衛（courseModal.js:42-55：點名分頁且 rollCallCompleted=false——照 legacy 只看完成旗標）。 */
  const needsRollCallGuard = () => activeTab === 'rollcall' && !rollCallCompleted;

  const switchTab = (next: CourseViewTab) => {
    if (next === activeTab) {
      return;
    }
    if (!needsRollCallGuard()) {
      // courseModal.js:70-71（非點名分頁直接切換，同步）。
      setActiveTab(next);
      return;
    }
    // courseModal.js:61-68：切離點名分頁的守衛（legacy 用瀏覽器 confirm()＝quirk，
    // 雛形一律 dialogs.confirm，語意相同）。
    void (async () => {
      const allowed = await dialogs.confirm('尚未存檔，是否確定要關閉?');
      if (allowed) {
        setActiveTab(next);
      }
    })();
  };

  const closeMain = () => {
    if (!needsRollCallGuard()) {
      onClose(); // :49-51/:52-54
      return;
    }
    void (async () => {
      const allowed = await dialogs.confirm('尚未存檔，是否確定要關閉?'); // :46
      if (allowed) {
        onClose();
      }
    })();
  };

  // ------------------------------------------------------------------
  // 課程表單：手動取單（courseFormTab.js:15-24 genCourseForm）
  // ------------------------------------------------------------------
  const [formsState, setFormsState] = useState<readonly Row[]>(() =>
    Array.isArray(row['forms']) ? (row['forms'] as readonly Row[]) : [],
  );
  const genForm = (targetId: string) => {
    void (async () => {
      try {
        const confirmed = await dialogs.confirm('是否確定要發表單給自己?'); // :18
        if (!confirmed) {
          return;
        }
        const target = formsState.find((entry) => String(entry['formTargetId'] ?? entry['id']) === targetId);
        const next: readonly Row[] = [
          ...formsState,
          {
            student: '（本人）',
            name: String(target?.['name'] ?? ''),
            dueDate: String(target?.['dueDate'] ?? ''),
            flow: '待填寫',
            status: '未完成',
            version: String(target?.['version'] ?? '1'),
          },
        ];
        await dialogs.processing.wrap(dataSource.update(pageId, rowKey, { forms: next }));
        setFormsState(next);
        await dialogs.info('表單已發給自己'); // legacy genCourseForm 回呼 showInfoDialog(msg)；雛形文字【雛形值待實機校準】
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };

  // ------------------------------------------------------------------
  // 課程公告：刪除（announcementTab.js:42）
  // ------------------------------------------------------------------
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [announcements, setAnnouncements] = useState<readonly Row[]>(() =>
    Array.isArray(row['announcements']) ? (row['announcements'] as readonly Row[]) : [],
  );
  const deleteAnnouncement = (index: number) => {
    void (async () => {
      const confirmed = await dialogs.confirm('是否確定要刪除?'); // :147
      if (!confirmed) {
        return;
      }
      const next = announcements.filter((_, position) => position !== index);
      try {
        await dialogs.processing.wrap(dataSource.update(pageId, rowKey, { announcements: next }));
        setAnnouncements(next);
        await dialogs.info('公告已刪除'); // legacy showInfoDialog(msg)；雛形文字【雛形值待實機校準】
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };

  // ------------------------------------------------------------------
  // 畫面
  // ------------------------------------------------------------------
  const infoLabel = (text: string, value: ReactElement | string, required = false) => (
    <div className="flex items-start gap-2">
      <span className="w-40 shrink-0 text-right text-sm">
        {required ? <span className="text-danger">*</span> : null}
        {text}
      </span>
      <span className="text-sm">{value}</span>
    </div>
  );

  const teachers = Array.isArray(row['classTeacherList']) ? (row['classTeacherList'] as readonly Row[]) : [];
  const attachFiles = Array.isArray(row['attachFiles']) ? (row['attachFiles'] as readonly Row[]) : [];
  const refJobTypes = String(row['refJobTypeId'] ?? '')
    .split(',')
    .filter((value) => value.trim() !== '')
    .map((value) => labelOf(ACCOUNT_JOB_TYPE_OPTIONS, value))
    .filter((label) => label !== '');

  const formColumns: readonly TableColumn[] = [
    { key: 'student', header: '學生', align: 'center' },
    { key: 'name', header: '表單名稱', align: 'center' },
    { key: 'dueDate', header: '應完成日期', align: 'center' },
    { key: 'flow', header: '表單流程', align: 'center' },
    { key: 'status', header: '表單狀態', align: 'center' },
    { key: 'version', header: '表單版本', align: 'center' },
  ];

  return (
    <ModalFrame
      uiId={`${pageId}-course-view-dialog`}
      open
      title="檢視課程" // courseModal.jsp:80
      size="lg"
      onClose={closeMain}
      footer={
        <Button
          uiId={`${pageId}-course-view-dialog-footer-close`}
          variant="default"
          size="sm"
          type="button"
          onClick={closeMain}
        >
          {/* spring close＝關閉（properties:45） */}
          關閉
        </Button>
      }
    >
      <div data-ui-id={`${pageId}-course-view-body`} className="space-y-3">
        <input type="hidden" name="classInCourseId" value={rowKey} />
        {/* 分頁籤（courseModal.jsp:70-92）。 */}
        <div className="flex flex-wrap gap-1">
          {TAB_LABELS.map((tab) => (
            <button
              key={tab.key}
              data-ui-id={`${pageId}-course-view-tab-${tab.key}`}
              type="button"
              className={`px-3 py-1 text-sm font-medium ${
                activeTab === tab.key ? 'bg-green-700 text-white' : 'bg-white text-gray-400'
              }`}
              onClick={() => {
                switchTab(tab.key);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 課程資訊（courseInfoTemplate.jsp；唯讀 label 群）。 */}
        {activeTab === 'info' ? (
          <div data-ui-id={`${pageId}-course-view-info`} className="space-y-1">
            {infoLabel('類型：', labelOf(COURSE_CLAZZ_KIND_OPTIONS, String(row['kind'] ?? '')), true)}
            {infoLabel('主要訓練類別：', labelOf(ACCOUNT_JOB_TYPE_OPTIONS, String(row['jobTypeId'] ?? '')), true)}
            {infoLabel('其他參與訓練類別：', refJobTypes.join(','))}
            {infoLabel('部門：', String(row['unit'] ?? ''), true)}
            {infoLabel('課程樣板：', String(row['title'] ?? ''))}
            {infoLabel('課程類別：', String(row['category3'] ?? ''), true)}
            {infoLabel('必/選修：', String(row['category2'] ?? ''), true)}
            {infoLabel('課程代碼：', String(row['id'] ?? ''), true)}
            {infoLabel('課程名稱：', String(row['title'] ?? ''), true)}
            {infoLabel('課程日期：', `${String(row['date'] ?? '')} ${String(row['beginTime'] ?? '')}~${String(row['endTime'] ?? '')}`, true)}
            {infoLabel(
              '開放報名：',
              String(row['isSignUp'] ?? '0') === '1' ? '是' : '否',
              true,
            )}
            {infoLabel('報名期間：', String(row['signUpPeriod'] ?? ''), true)}
            {infoLabel(
              '跨領域課程：',
              String(row['isCrossDomain'] ?? '') === '1' ? '※允許跨職類上課' : '',
              true,
            )}
            {infoLabel('學員人數上限：', `${String(row['attendanceQty'] ?? '')}人`, true)}
            {infoLabel('給予時數：', `${String(row['qty'] ?? '')}小時`, true)}
            {infoLabel('地點：', labelOf(COURSE_CLAZZ_LOCATION_OPTIONS, String(row['locationId'] ?? '')), true)}
            {infoLabel(
              '教師清單：',
              <span className="inline-flex flex-wrap gap-1">
                {teachers.map((teacher, index) => (
                  <span key={`${String(teacher['teacherId'] ?? '')}-${index}`} className="text-sm font-bold">
                    {String(teacher['teacherName'] ?? '')}
                    <span className="ml-1 text-xs font-normal text-gray-500">
                      {Number(teacher['type'] ?? 1) === 1 ? '授課教師' : '協同教師'}
                    </span>
                  </span>
                ))}
              </span>,
            )}
            {infoLabel('外部講師：', String(row['visitingInstructor'] ?? ''))}
            {infoLabel('課程管理人員：', String(row['formOwnerName'] ?? ''))}
            {infoLabel('評核方式(複選)：', String(row['actionType'] ?? ''))}
            {/* 附檔表（courseModal.js:139-168 initClassAttachFileTable；查詢列無 attachFiles 種子 → 空＋註記）。 */}
            <Table
              uiId={`${pageId}-course-view-attach-table`}
              columns={[
                { key: 'name', header: '檔案名稱', align: 'center' },
                { key: 'size', header: '檔案大小', align: 'center' },
                { key: 'action', header: '', align: 'center' },
              ]}
              rows={attachFiles as readonly TableRow[]}
              rowKey={(entry, index) => `${String(entry['fileId'] ?? `attach-${index}`)}`}
              emptyText="查無符合的資料"
              renderCell={(entry, columnKey) => {
                if (columnKey === 'name') {
                  return String(entry['name'] ?? '');
                }
                if (columnKey === 'size') {
                  return String(entry['size'] ?? '');
                }
                return (
                  <Button
                    uiId={`${pageId}-course-view-attach-download-${String(entry['fileId'] ?? '')}`}
                    variant="default"
                    size="sm"
                    type="button"
                    onClick={() => {
                      void dialogs.info('檔案下載（雛形不落地【雛形值待實機校準】）');
                    }}
                  >
                    下載
                  </Button>
                );
              }}
            />
          </div>
        ) : null}

        {/* 課程表單（tabs/courseFormTab.jsp＋courseFormTab.js）。 */}
        {activeTab === 'form' ? (
          <div data-ui-id={`${pageId}-course-view-form`} className="space-y-2">
            <div>
              <div className="text-sm font-bold">手動取單</div> {/* courseFormTab.jsp:10 */}
              <div data-ui-id={`${pageId}-course-view-form-list`} className="space-y-1">
                {/* legacy genForm 按鈕列（courseFormTab.js:95-103 form-target；種子以表單名稱列）。 */}
                {[...new Set(formsState.map((entry) => String(entry['name'] ?? '')))].map((name) => (
                  <div key={name} className="flex items-center gap-2 text-sm">
                    {'\u00a0\u00a0'}
                    {name}
                    <Button
                      uiId={`${pageId}-course-view-gen-form-${name}`}
                      variant="default"
                      size="sm"
                      type="button"
                      dataEffect={`update:${pageId}`}
                      onClick={() => {
                        genForm(
                          String(
                            formsState.find((entry) => String(entry['name'] ?? '') === name)?.['formTargetId'] ??
                              name,
                          ),
                        );
                      }}
                    >
                      發送表單
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <Table
              uiId={`${pageId}-course-view-form-table`}
              columns={formColumns}
              rows={formsState as readonly TableRow[]}
              rowKey={(entry, index) => `${String(entry['name'] ?? index)}-${index}`}
              emptyText="查無符合的資料"
              renderCell={(entry, columnKey) => String(entry[columnKey] ?? '')}
            />
          </div>
        ) : null}

        {/* 課程公告（tabs/announcementTab.jsp＋announcementTab.js）。 */}
        {activeTab === 'announcement' ? (
          <div data-ui-id={`${pageId}-course-view-announcement`} className="space-y-2">
            <Button
              uiId={`${pageId}-course-view-announcement-add`}
              variant="primary"
              size="sm"
              type="button"
              onClick={() => {
                setAnnouncementOpen(true); // announcementTab.js:31-35
              }}
            >
              新增 {/* :40-43 */}
            </Button>
            <Table
              uiId={`${pageId}-course-view-announcement-table`}
              columns={[
                { key: 'action', header: '', align: 'center' },
                { key: 'status', header: '公告狀態', align: 'center' },
                { key: 'subject', header: '主旨', align: 'center' },
                { key: 'date', header: '發佈日期', align: 'center' },
              ]}
              rows={announcements as readonly TableRow[]}
              rowKey={(entry, index) => `announcement-${String(entry['subject'] ?? index)}-${index}`}
              emptyText="查無符合的資料"
              renderCell={(entry, columnKey) => {
                if (columnKey === 'action') {
                  const index = announcements.findIndex(
                    (item) => String(item['subject'] ?? '') === String(entry['subject'] ?? ''),
                  );
                  return (
                    <Button
                      uiId={`${pageId}-course-view-announcement-delete-${index}`}
                      variant="danger"
                      size="sm"
                      type="button"
                      // C5-j：刪除真的 update announcements（與查詢頁分立 modal 同標註，複驗 F1）。
                      dataEffect={`update:${pageId}`}
                      onClick={() => {
                        deleteAnnouncement(index);
                      }}
                    >
                      刪除
                    </Button>
                  );
                }
                return String(entry[columnKey] ?? '');
              }}
            />
          </div>
        ) : null}

        {/* 課堂點名（rollCallTab；本體重用 RollCallPanel）。 */}
        {activeTab === 'rollcall' ? (
          <div data-ui-id={`${pageId}-course-view-rollcall`}>
            <div className="mb-2">
              <Button
                uiId={`${pageId}-course-view-rollcall-save`}
                variant="primary"
                size="sm"
                type="button"
                dataEffect={`update:${pageId}`}
                onClick={() => {
                  rollCallSaveRef.current?.();
                }}
              >
                儲存 {/* rollCallTab.jsp:60（聚合分頁的儲存鈕） */}
              </Button>
            </div>
            <RollCallPanel
              pageId={pageId}
              row={row}
              saveRef={rollCallSaveRef}
              onSaved={() => {
                setRollCallCompleted(true); // rollCallTab.js:227-231 rollCallCompleted = true
                void dataSource
                  .update(pageId, rowKey, { rollCallCompleted: '1' })
                  .catch(() => undefined); // 完成旗標存回列上
                void dialogs.info('儲存成功'); // legacy saveRollCall 回呼 showInfoDialog(msg)
              }}
            />
          </div>
        ) : null}
      </div>

      {/* 公告新增/修改（複用批次 8 AnnouncementModal；疊放語意（見批次 10 D7））。 */}
      {announcementOpen ? (
        <AnnouncementModal
          pageId={pageId}
          row={row}
          onClose={() => {
            setAnnouncementOpen(false);
          }}
          onSaved={() => {
            setAnnouncementOpen(false);
          }}
        />
      ) : null}
    </ModalFrame>
  );
}

/**
 * 行事曆項目（course-clazz-query-list 的行事曆活動點擊＝CourseModal.openModal(id,'courseQuery')，
 * clazzQueryList.js:1177；B2-3 的 legacy 入口）。開課管理的行事曆維持 openEdit（showClass）。
 */
export function CourseViewCalendarItem({ row, ctx }: { row: Row; ctx: BehaviorFlowContext }): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-ui-id={`${ctx.pageId}-calendar-item-view-${toKebabLocal(String(row.id ?? ''))}`}
        className={`${LINK_TEXT_CLASS} cursor-pointer`}
        onClick={() => {
          setOpen(true);
        }}
      >
        {String(row['title'] ?? '')}
      </button>
      {open ? (
        <CourseViewModal
          pageId={ctx.pageId}
          row={row}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * 首頁模式（B2-6；clazzQueryList.js:805-825／clazzQueryList_js.jsp:1588-1617）：
 * 標題「最近一個月可報名課程」＋說明 li 逐字＋僅列固定今天起一個月內的開放報名課程
 * （legacy queryClassUrl='tsghfunc/getClassListForHomepageAjax' 的伺服器過濾，雛形在畫面層過濾，
 * 差異記進度檔；查詢區欄位隱藏/日期鎖定不重現）。關閉＝回到一般列表（CourseCalendarToggle）。
 */
export function CourseQueryTableArea({
  children,
  ctx,
  rows,
}: {
  children: ReactElement;
  ctx: BehaviorFlowContext;
  rows: readonly Row[];
}): ReactElement {
  const [homeMode, setHomeMode] = useState(false);
  // 首頁模式清單項目可點（legacy 首頁沿用同一 DataTable、名稱連結 showEdit→showClass，
  // clazzQueryList.js:119-122/:38-41；雛形開「課程資訊」唯讀 modal＝查詢頁同路徑）。
  const [viewing, setViewing] = useState<Row | null>(null);
  const homeRows = rows.filter(
    (row) =>
      String(row['isSignUp'] ?? '') === '1' &&
      String(row['date'] ?? '') >= HOME_NOW &&
      String(row['date'] ?? '') <= HOME_WINDOW_END,
  );
  if (!homeMode) {
    return (
      <div data-ui-id={`${ctx.pageId}-display-host`}>
        <div className="mb-2">
          <Button
            uiId={`${ctx.pageId}-home-mode`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              setHomeMode(true);
            }}
          >
            首頁模式（最近一個月可報名課程）
          </Button>
        </div>
        {children}
      </div>
    );
  }
  return (
    <div data-ui-id={`${ctx.pageId}-home-mode-area`} className="space-y-2">
      <div className="flex items-center gap-2">
        <span data-ui-id={`${ctx.pageId}-home-mode-title`} className="text-base font-medium">
          最近一個月可報名課程
        </span>
        <Button
          uiId={`${ctx.pageId}-home-mode-exit`}
          variant="default"
          size="sm"
          type="button"
          onClick={() => {
            setHomeMode(false);
          }}
        >
          回列表
        </Button>
      </div>
      <div data-ui-id={`${ctx.pageId}-home-mode-notice`} className="rounded border border-border p-2 text-sm">
        <ul className="ml-4 list-disc">
          <li>
            本功能僅呈現未來一個月被安排的訓練科部所開設的開放報名課程，其它的課程請至
            課程專區-&gt;課程查詢 查詢
          </li>
        </ul>
      </div>
      <ul data-ui-id={`${ctx.pageId}-home-mode-list`} className="ml-4 list-disc">
        {homeRows.map((row) => (
          <li
            key={String(row.id ?? '')}
            data-ui-id={`${ctx.pageId}-home-mode-item-${toKebabLocal(String(row.id ?? ''))}`}
          >
            <button
              type="button"
              data-ui-id={`${ctx.pageId}-home-mode-item-link-${toKebabLocal(String(row.id ?? ''))}`}
              className={`${LINK_TEXT_CLASS} cursor-pointer`}
              onClick={() => {
                setViewing(row); // clazzQueryList.js:38-41 showEdit → 課程資訊
              }}
            >
              {String(row['title'] ?? '')}（{String(row['date'] ?? '')}）
            </button>
          </li>
        ))}
      </ul>
      {viewing !== null ? (
        <CourseInfoModal
          pageId={ctx.pageId}
          row={viewing}
          onClose={() => {
            setViewing(null);
          }}
        />
      ) : null}
    </div>
  );
}
